// Customer Groups data layer (Phase 4, P4-WP11b) — named customer groups (each can
// point at a default price list) + the two customer-link setters. The SINGLE interface
// between the app and public.customer_groups, plus the two additive assignment columns
// on public.customers (price_list_id / customer_group_id), added in
// supabase/migrations/20260721130000_price_lists.sql.
//
// A group carries no financial history, so a hard delete is fine — the FK SET NULLs the
// link on any customer that referenced it. The link setters write ONLY the two new
// customer columns; they never touch any other customer field or write path.
//
// company_id is owned + frozen by DB triggers; we never send it.

import { supabase } from "./supabaseClient";

const CG = "customer_groups";
const CUST = "customers";
const SELECT = "id, name, price_list_id, notes, created_at, updated_at";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

function toError(error, fallback, dup) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = dup || "A group with that name already exists."; break;
    case "23503": message = "That price list, group, or customer does not exist."; break;
    case "23502": message = "A required field is missing."; break;
    case "42501": message = "You do not have access to this record."; break;
    case "PGRST116": message = "Not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Customer groups ─────────────────────────────────────────────────────────────

/** All groups for the company, ordered by name (with member_count). */
export async function getCustomerGroups() {
  const { data, error } = await client().from(CG).select(`${SELECT}, customers ( count )`).order("name", { ascending: true });
  if (error) throw toError(error, "Could not load customer groups");
  return (data || []).map(({ customers, ...g }) => ({ ...g, member_count: customers?.[0]?.count ?? 0 }));
}

/** One group (or null if not found / not visible). */
export async function getCustomerGroup(id) {
  const { data, error } = await client().from(CG).select(SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load customer group");
  return data || null;
}

export async function createCustomerGroup(draft = {}) {
  if (!draft.name || !String(draft.name).trim()) throw new Error("Group name is required.");
  const row = { name: String(draft.name).trim(), price_list_id: draft.price_list_id || null, notes: draft.notes || null };
  const { data, error } = await client().from(CG).insert(row).select(SELECT).single();
  if (error) throw toError(error, "Could not create customer group");
  return data;
}

export async function updateCustomerGroup(id, patch = {}) {
  const upd = {};
  if (patch.name !== undefined) upd.name = String(patch.name).trim();
  if (patch.price_list_id !== undefined) upd.price_list_id = patch.price_list_id || null;
  if (patch.notes !== undefined) upd.notes = patch.notes || null;
  const { data, error } = await client().from(CG).update(upd).eq("id", id).select(SELECT).single();
  if (error) throw toError(error, "Could not update customer group");
  return data;
}

/** Hard delete — customers that referenced this group have their link SET NULL by the FK. */
export async function deleteCustomerGroup(id) {
  const { error } = await client().from(CG).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete customer group");
}

// ── Customer link setters (write ONLY the two new customer columns) ──────────────

/** Assign (or clear) a customer's group. Pass null to unassign. */
export async function assignCustomerToGroup(customerId, groupId) {
  const { data, error } = await client().from(CUST)
    .update({ customer_group_id: groupId || null }).eq("id", customerId).select("id, customer_group_id").single();
  if (error) throw toError(error, "Could not assign the customer to the group");
  return data;
}

/** Assign (or clear) a customer's own price list. Pass null to clear. */
export async function setCustomerPriceList(customerId, priceListId) {
  const { data, error } = await client().from(CUST)
    .update({ price_list_id: priceListId || null }).eq("id", customerId).select("id, price_list_id").single();
  if (error) throw toError(error, "Could not set the customer's price list");
  return data;
}
